import React, { useEffect, useState } from "react";
import styles from './CaseLogList.module.scss';
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import CaseLogCard from "../../../components/caseLogCard/CaseLogCard";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import LoadMore from "../../../components/loadMore/loadMore";
import Modal from "../../../components/modal/Modal";
import Button from "../../../components/button/Button";
import { format } from "date-fns";

const CaseLogList = () => {
  const limit = 20;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [caseLogs, setCaseLogs] = useState([]);
  const [student, setStudent] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caseLogId, setCaseLogId] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const [logReportStatus, setLogReportStatus] = useState(null);
  const [pendingCaseCount, setPendingCaseCount] = useState(0);
  const [submittedCaseCount, setSubmittedCaseCount] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const { logReportId, studentId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if(page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadMoreLoading(true);
      }
      await axiosClient.get(`case-log`, {
        params: {
          logReportId: logReportId,
          limit: limit,
          page: page,
        }
      })
      .then((response) => {
        if(response.data?.student){
          setStudent(response.data.student)
        }
        if(response.data?.caseLogs) {
          if(page === 1) {
            setCaseLogs([]);
          }
          setCaseLogs((prevData) => {
            return [...prevData, ...response.data.caseLogs.map((caseLog) => {
              return {
                id: caseLog.id,
                data: [
                  {
                    label: 'Case Date',
                    text: caseLog.dateOfCase ? format(caseLog.dateOfCase, 'MM/dd/yyyy') : null
                  },
                  {
                    label: 'Start Time',
                    text: caseLog.startTime
                  },
                  {
                    label: 'Case Count',
                    text: caseLog.caseCount
                  },
                  {
                    label: 'Clinical Site',
                    text: caseLog.clinicalSite?.name ?? ''
                  },
                ],
                cta: caseLog.status !== 'Deleted' ? {
                  label: 'View Cases Details',
                  link: `${studentId ? `/log-reports/student/${studentId}` : ''}/log-reports/${logReportId}/case-logs/${caseLog.id}`
                } : null,
                status: caseLog.status
              }
            })];
          });
          setTotalPages(response.data.totalPages);
        }
        if(response.data?.logReport) {
          setLogReportStatus(response.data?.logReport?.status);
        }
        if(response.data?.pendingCaseCount !== undefined) {
          console.log(response.data?.pendingCaseCount);
          setPendingCaseCount(response.data?.pendingCaseCount)
        }
        if(response.data?.submittedCaseCount !== undefined) {
          setSubmittedCaseCount(response.data?.submittedCaseCount)
        }
      })
      .catch((error) => {
        if(error.status === 404) {
          navigate('/404');
        } else {
          showToast(error.message, 'error');
        }
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadMoreLoading(false);
      });
    };

    fetchData();
  }, [logReportId, page, showToast, refetchTrigger, navigate, studentId]);

  const loadMore = async () => {
    setPage(page + 1);
  };

  const handleDelete = async () => {
    setIsDeleteLoading(true);
    await axiosClient.delete(`case-log`, {
      params: {
        id: caseLogId
      }
    })
    .then((response) => {
      setShowDeleteModal(false);
      showToast(response.message);
      setRefetchTrigger(prev => !prev);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsDeleteLoading(false);
    });
  };

  const handleCreateCase = async () => {
    setIsLoading(true);
    await axiosClient.post(`case-log/create`, {
      logReportId
    })
    .then((response) => {
      navigate(`/log-reports/${response?.data?.logReport?.id}/case-logs/${response?.data?.id}`)
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    await axiosClient.patch(`log-reports/${logReportId}/submit`)
    .then((response) => {
      showToast(response.message);
      navigate(`/log-reports`);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={studentId ? [
            { label: 'Log Reports', path: '/log-reports' },
            { label: `${student.firstName ?? ''} ${student.lastName ?? ''}`, path: `/log-reports/student/${studentId}` },
            { label: `Log Report - ${logReportId}` }
          ] : [
            { label: 'Log Reports', path: '/log-reports' },
            { label: `Log Report - ${logReportId}` }
          ]}
        />
        {logReportStatus !== 'Completed' && !isLoading && <div className={styles.buttons}>
          {pendingCaseCount === 0 && <Button
            label="Add New Case"
            size="medium"
            onClick={handleCreateCase}
          />}
          {pendingCaseCount === 0 && submittedCaseCount > 0 && <Button
            label="Submit Log Report"
            size="medium"
            onClick={() => setShowSubmitModal(true)}
          />}
        </div>}
      </div>
      <div className={styles.container}>
        {caseLogs.length > 0 ? <div className={styles.cardList}>
          {caseLogs.map((caseLog, index) => {
            return (
              <div className={styles.cardItem} key={`caseLog-${index}`}>
                <CaseLogCard
                  {...caseLog}
                  canDelete={logReportStatus !== 'Completed' && caseLog.status !== 'Deleted'}
                  onDelete={() => {
                    setCaseLogId(caseLog.id);
                    setShowDeleteModal(true);
                  }}
                />
              </div>
            );
          })}
          {(isLoadMoreLoading || page < totalPages) 
            && <LoadMore isLoading={isLoadMoreLoading} onClick={loadMore} />}
        </div> : !isLoading && <div className={styles.emptyLogs}>
          <p>You have no case logs at this time.</p>
        </div>}
      </div>
      {showDeleteModal && <Modal>
        <div className={styles.deleteModal}>
          {isDeleteLoading && <LoadingOverlay />}
          <h2 className={styles.modalTitle}>This action cannot be undone. Are you sure you want to delete this case?</h2>
          <div className={styles.buttons}>
            <Button
              label="Delete"
              size="small"
              variant="secondary"
              onClick={() => {
                handleDelete();
              }}
            />
            <Button
              label="Cancel"
              size="small"
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            />
          </div>
        </div>
      </Modal>}
      {showSubmitModal && <Modal>
        <div className={styles.submitModal}>
          <p className={styles.activeCaseText}>Active Cases: <span>{pendingCaseCount + submittedCaseCount}</span></p>
          <h2 className={styles.modalTitle}>Are you sure you want to submit log report?</h2>
          <div className={styles.buttons}>
            <Button
              label="Cancel"
              size="small"
              variant="white"
              onClick={() => setShowSubmitModal(false)}
            />
            <Button
              label="Submit"
              size="small"
              onClick={() => handleSubmit()}
            />
          </div>
        </div>
      </Modal>}
    </>
  );
};

export default CaseLogList;